import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  listWaPromptTemplates,
  upsertWaPromptTemplate,
  setWaPromptTemplateArchived,
  setWaPromptTemplateActive,
  reorderWaPromptTemplates,
} from "@/lib/whatsapp-prompt-templates.functions";
import { Archive, ArchiveRestore, ChevronDown, ChevronUp, Loader2, Pencil, Plus } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/whatsapp-templates")({
  head: () => ({
    meta: [
      { title: "Admin — WhatsApp-sjablonen" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminWaTemplatesPage,
});

type Row = {
  id: string;
  slug: string;
  label: string;
  body: string;
  sort_order: number;
  is_active: boolean;
  archived_at: string | null;
  updated_at: string;
};

type Draft = {
  id?: string;
  slug: string;
  label: string;
  body: string;
  sort_order: number;
  is_active: boolean;
};

const emptyDraft = (): Draft => ({
  slug: "",
  label: "",
  body: "",
  sort_order: 100,
  is_active: true,
});

const PREVIEW_VARS: Record<string, string> = {
  "{greeting}": "Hallo Jan",
  "{orderPart}": " voor bestelling EVG-12345",
  "{wherePart}": " op Voorbeeldstraat 1, Amsterdam",
  "{whenPart}": " van dinsdag 8 juli",
  "{firstName}": "Jan",
  "{orderNumber}": "EVG-12345",
  "{location}": "Voorbeeldstraat 1, Amsterdam",
  "{date}": "dinsdag 8 juli",
};

function renderPreview(body: string): string {
  let out = body;
  for (const [k, v] of Object.entries(PREVIEW_VARS)) out = out.split(k).join(v);
  return out;
}

function AdminWaTemplatesPage() {
  const { isAdmin, loading } = useAuth();
  const list = useServerFn(listWaPromptTemplates);
  const upsert = useServerFn(upsertWaPromptTemplate);
  const archive = useServerFn(setWaPromptTemplateArchived);
  const setActive = useServerFn(setWaPromptTemplateActive);
  const reorder = useServerFn(reorderWaPromptTemplates);
  const qc = useQueryClient();

  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const [showArchived, setShowArchived] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-wa-templates"],
    queryFn: () => list({ data: { includeArchived: true } }),
    enabled: isAdmin,
  });

  const rows: Row[] = useMemo(() => (data?.items ?? []) as Row[], [data]);
  const visible = showArchived ? rows : rows.filter((r) => !r.archived_at);

  const saveMut = useMutation({
    mutationFn: (d: Draft) => upsert({ data: d }),
    onSuccess: (r: any) => {
      toast.success(r.created ? "Sjabloon aangemaakt" : "Sjabloon bijgewerkt");
      qc.invalidateQueries({ queryKey: ["admin-wa-templates"] });
      setDraft(emptyDraft());
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const archiveMut = useMutation({
    mutationFn: (v: { id: string; archived: boolean }) => archive({ data: v }),
    onSuccess: (_r, v) => {
      toast.success(v.archived ? "Gearchiveerd" : "Uit archief teruggezet");
      qc.invalidateQueries({ queryKey: ["admin-wa-templates"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const activeMut = useMutation({
    mutationFn: (v: { id: string; is_active: boolean }) => setActive({ data: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-wa-templates"] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const reorderMut = useMutation({
    mutationFn: (orderedIds: string[]) => reorder({ data: { orderedIds } }),
    onMutate: async (orderedIds) => {
      await qc.cancelQueries({ queryKey: ["admin-wa-templates"] });
      const prev = qc.getQueryData<any>(["admin-wa-templates"]);
      if (prev?.items) {
        const byId = new Map<string, any>(prev.items.map((r: any) => [r.id, r]));
        const reordered = orderedIds
          .map((id, i) => {
            const row = byId.get(id);
            return row ? { ...row, sort_order: (i + 1) * 10 } : null;
          })
          .filter(Boolean);
        // Behoud rijen die niet in de zichtbare lijst zaten (bv. gearchiveerd),
        // achteraan op oorspronkelijke volgorde.
        const untouched = prev.items.filter((r: any) => !orderedIds.includes(r.id));
        qc.setQueryData(["admin-wa-templates"], { ...prev, items: [...reordered, ...untouched] });
      }
      return { prev };
    },
    onError: (e: Error, _v, ctx: any) => {
      if (ctx?.prev) qc.setQueryData(["admin-wa-templates"], ctx.prev);
      toast.error(e.message || "Kon volgorde niet opslaan");
    },
    onSuccess: () => toast.success("Volgorde opgeslagen"),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin-wa-templates"] }),
  });

  function moveRow(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= visible.length) return;
    const ids = visible.map((r) => r.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorderMut.mutate(ids);
  }

  if (loading) return <p>Laden…</p>;
  if (!isAdmin) return <p className="text-[#3a7bd5]">Geen toegang.</p>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">WhatsApp-teksttemplates</h1>
        <p className="text-sm text-muted-foreground">
          Sjablonen voor de tevredenheids-WhatsApp na een afgeronde klus. Bekende
          slugs (<code>standaard</code>, <code>zakelijk</code>, <code>camera</code>,{" "}
          <code>groepenkast</code>, <code>laadpaal</code>, <code>service</code>)
          worden automatisch voorgeselecteerd; extra slugs blijven handmatig kiesbaar.
        </p>
      </div>

      {/* Editor */}
      <div className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold">
          {draft.id ? (
            <>
              <Pencil className="size-4" /> Sjabloon bewerken
            </>
          ) : (
            <>
              <Plus className="size-4" /> Nieuw sjabloon
            </>
          )}
        </h2>
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <Label>Slug (uniek, kleine letters)</Label>
            <Input
              value={draft.slug}
              onChange={(e) =>
                setDraft((d) => ({ ...d, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") }))
              }
              placeholder="bijv. zonnepanelen"
              disabled={!!draft.id}
            />
          </div>
          <div>
            <Label>Label (zichtbaar in dialoog)</Label>
            <Input
              value={draft.label}
              onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
              placeholder="bijv. Zonnepanelen"
            />
          </div>
          <div>
            <Label>Sorteervolgorde</Label>
            <Input
              type="number"
              value={draft.sort_order}
              onChange={(e) => setDraft((d) => ({ ...d, sort_order: Number(e.target.value) || 0 }))}
            />
          </div>
          <div className="flex items-end gap-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.is_active}
                onChange={(e) => setDraft((d) => ({ ...d, is_active: e.target.checked }))}
              />
              Actief (kiesbaar in dialoog)
            </label>
          </div>
        </div>

        <div className="mt-3">
          <Label>Berichttekst</Label>
          <Textarea
            value={draft.body}
            onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
            rows={5}
            placeholder="{greeting}, dit is Elvion Group Nederland. We hebben de werkzaamheden{orderPart}{wherePart}{whenPart} zojuist afgerond..."
          />
          <p className="mt-1 text-[11px] text-muted-foreground">
            Variabelen: <code>{"{greeting}"}</code> <code>{"{orderPart}"}</code>{" "}
            <code>{"{wherePart}"}</code> <code>{"{whenPart}"}</code>{" "}
            <code>{"{firstName}"}</code> <code>{"{orderNumber}"}</code>{" "}
            <code>{"{location}"}</code> <code>{"{date}"}</code>
          </p>
        </div>

        {draft.body && (
          <div className="mt-3 rounded-md border bg-emerald-50/40 p-3">
            <div className="mb-1 text-[10px] uppercase tracking-wide text-emerald-800">
              Voorbeeld met dummy-variabelen
            </div>
            <div className="whitespace-pre-wrap text-sm">{renderPreview(draft.body)}</div>
          </div>
        )}

        <div className="mt-3 flex gap-2">
          <Button
            onClick={() => saveMut.mutate(draft)}
            disabled={saveMut.isPending || !draft.slug || !draft.label || draft.body.length < 10}
          >
            {saveMut.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
            {draft.id ? "Wijzigingen opslaan" : "Sjabloon aanmaken"}
          </Button>
          {draft.id && (
            <Button variant="outline" onClick={() => setDraft(emptyDraft())}>
              Annuleren
            </Button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="rounded-lg border bg-card">
        <div className="flex items-center justify-between border-b p-3">
          <h2 className="text-lg font-semibold">
            Sjablonen ({visible.length}
            {showArchived ? ` van ${rows.length}` : ""})
          </h2>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Toon gearchiveerde
          </label>
        </div>
        {isLoading ? (
          <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Laden…
          </div>
        ) : visible.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground">Geen sjablonen.</div>
        ) : (
          <ul className="divide-y">
            {visible.map((r, idx) => (
              <li key={r.id} className="grid gap-2 p-3 md:grid-cols-[auto_1fr_auto] md:items-start">
                <div className="flex flex-col gap-1" aria-label="Volgorde aanpassen">
                  <Button
                    size="icon"
                    variant="outline"
                    className="size-7"
                    aria-label="Omhoog"
                    title="Omhoog verplaatsen"
                    disabled={idx === 0 || reorderMut.isPending}
                    onClick={() => moveRow(idx, -1)}
                  >
                    <ChevronUp className="size-3.5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="outline"
                    className="size-7"
                    aria-label="Omlaag"
                    title="Omlaag verplaatsen"
                    disabled={idx === visible.length - 1 || reorderMut.isPending}
                    onClick={() => moveRow(idx, 1)}
                  >
                    <ChevronDown className="size-3.5" />
                  </Button>
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{r.label}</span>
                    <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px]">{r.slug}</code>
                    <span className="text-[10px] text-slate-500">volgorde {r.sort_order}</span>
                    {r.archived_at ? (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                        Gearchiveerd
                      </span>
                    ) : r.is_active ? (
                      <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800">
                        Actief
                      </span>
                    ) : (
                      <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700">
                        Inactief
                      </span>
                    )}
                  </div>
                  <p className="mt-1 line-clamp-3 whitespace-pre-wrap text-xs text-slate-700">{r.body}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setDraft({
                        id: r.id,
                        slug: r.slug,
                        label: r.label,
                        body: r.body,
                        sort_order: r.sort_order,
                        is_active: r.is_active,
                      })
                    }
                  >
                    <Pencil className="mr-1 size-3.5" /> Bewerken
                  </Button>
                  {!r.archived_at && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => activeMut.mutate({ id: r.id, is_active: !r.is_active })}
                    >
                      {r.is_active ? "Deactiveren" : "Activeren"}
                    </Button>
                  )}
                  {r.archived_at ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => archiveMut.mutate({ id: r.id, archived: false })}
                    >
                      <ArchiveRestore className="mr-1 size-3.5" /> Uit archief
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => archiveMut.mutate({ id: r.id, archived: true })}
                    >
                      <Archive className="mr-1 size-3.5" /> Archiveren
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <AutoRulesEditor />

      <AuditLogViewer />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Auto-selectieregels: koppelt klanttype + keyword-patroon aan template-slug.
// ---------------------------------------------------------------------------

import {
  listWaAutoRules,
  upsertWaAutoRule,
  deleteWaAutoRule,
} from "@/lib/whatsapp-auto-rules.functions";
import { Trash2 } from "lucide-react";

type AutoRuleRow = {
  id: string;
  sort_order: number;
  match_customer_type: "consumer" | "business" | null;
  keyword_pattern: string | null;
  template_slug: string;
  description: string;
  is_active: boolean;
  updated_at: string;
};

type AutoRuleDraft = {
  id?: string;
  sort_order: number;
  match_customer_type: "consumer" | "business" | null;
  keyword_pattern: string;
  template_slug: string;
  description: string;
  is_active: boolean;
};

const emptyRuleDraft = (): AutoRuleDraft => ({
  sort_order: 100,
  match_customer_type: null,
  keyword_pattern: "",
  template_slug: "",
  description: "",
  is_active: true,
});

function AutoRulesEditor() {
  const list = useServerFn(listWaAutoRules);
  const upsert = useServerFn(upsertWaAutoRule);
  const del = useServerFn(deleteWaAutoRule);
  const qc = useQueryClient();

  const [draft, setDraft] = useState<AutoRuleDraft>(emptyRuleDraft());

  const rulesQ = useQuery({
    queryKey: ["admin-wa-auto-rules"],
    queryFn: () => list({ data: { includeInactive: true } }),
  });
  const rows: AutoRuleRow[] = useMemo(() => (rulesQ.data?.items ?? []) as AutoRuleRow[], [rulesQ.data]);

  const saveMut = useMutation({
    mutationFn: (d: AutoRuleDraft) =>
      upsert({
        data: {
          id: d.id,
          sort_order: d.sort_order,
          match_customer_type: d.match_customer_type,
          keyword_pattern: d.keyword_pattern.trim() || null,
          template_slug: d.template_slug.trim(),
          description: d.description.trim(),
          is_active: d.is_active,
        },
      }),
    onSuccess: (r: any) => {
      toast.success(r.created ? "Regel aangemaakt" : "Regel bijgewerkt");
      qc.invalidateQueries({ queryKey: ["admin-wa-auto-rules"] });
      qc.invalidateQueries({ queryKey: ["wa-auto-rules-active"] });
      setDraft(emptyRuleDraft());
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      toast.success("Regel verwijderd");
      qc.invalidateQueries({ queryKey: ["admin-wa-auto-rules"] });
      qc.invalidateQueries({ queryKey: ["wa-auto-rules-active"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="rounded-lg border bg-card">
      <div className="border-b p-3">
        <h2 className="text-lg font-semibold">Auto-selectieregels</h2>
        <p className="text-xs text-muted-foreground">
          Bepaalt welke template-slug automatisch wordt voorgesteld op basis van klanttype en
          een keyword-patroon (regex, case-insensitive) op klus/locatie. Volgorde bepaalt de
          prioriteit — de eerste matchende regel wint.
        </p>
      </div>

      <div className="grid gap-3 border-b p-3 md:grid-cols-6">
        <div className="md:col-span-1">
          <Label>Volgorde</Label>
          <Input
            type="number"
            value={draft.sort_order}
            onChange={(e) => setDraft((d) => ({ ...d, sort_order: Number(e.target.value) || 0 }))}
          />
        </div>
        <div className="md:col-span-1">
          <Label>Klanttype</Label>
          <select
            className="h-9 w-full rounded-md border bg-background px-2 text-sm"
            value={draft.match_customer_type ?? ""}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                match_customer_type: (e.target.value || null) as any,
              }))
            }
          >
            <option value="">Elk</option>
            <option value="consumer">Consument</option>
            <option value="business">Zakelijk</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <Label>Keyword-patroon (regex, leeg = geen keyword-eis)</Label>
          <Input
            value={draft.keyword_pattern}
            onChange={(e) => setDraft((d) => ({ ...d, keyword_pattern: e.target.value }))}
            placeholder="bijv. (camera|cctv|nvr)"
          />
        </div>
        <div className="md:col-span-1">
          <Label>Template-slug</Label>
          <Input
            value={draft.template_slug}
            onChange={(e) =>
              setDraft((d) => ({ ...d, template_slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") }))
            }
            placeholder="camera"
          />
        </div>
        <div className="md:col-span-1 flex items-end">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.is_active}
              onChange={(e) => setDraft((d) => ({ ...d, is_active: e.target.checked }))}
            />
            Actief
          </label>
        </div>
        <div className="md:col-span-5">
          <Label>Omschrijving (getoond in tooltip / uitleg)</Label>
          <Input
            value={draft.description}
            onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            placeholder="Camerabewaking (CCTV, NVR, Hikvision, Dahua)"
          />
        </div>
        <div className="flex items-end gap-2 md:col-span-1">
          <Button
            onClick={() => saveMut.mutate(draft)}
            disabled={saveMut.isPending || !draft.template_slug || !draft.description}
          >
            {saveMut.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
            {draft.id ? "Opslaan" : "Toevoegen"}
          </Button>
          {draft.id && (
            <Button variant="outline" onClick={() => setDraft(emptyRuleDraft())}>
              Annuleren
            </Button>
          )}
        </div>
      </div>

      {rulesQ.isLoading ? (
        <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Laden…
        </div>
      ) : rows.length === 0 ? (
        <div className="p-4 text-sm text-muted-foreground">Nog geen regels — voeg er hierboven een toe.</div>
      ) : (
        <ul className="divide-y">
          {rows.map((r) => (
            <li key={r.id} className="grid gap-2 p-3 md:grid-cols-[1fr_auto] md:items-start">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[10px] text-slate-500">#{r.sort_order}</span>
                  <span className="font-medium">{r.description}</span>
                  <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px]">→ {r.template_slug}</code>
                  {r.match_customer_type && (
                    <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-800">
                      {r.match_customer_type}
                    </span>
                  )}
                  {!r.is_active && (
                    <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700">
                      Inactief
                    </span>
                  )}
                </div>
                {r.keyword_pattern && (
                  <code className="mt-1 block text-[11px] text-slate-600">{r.keyword_pattern}</code>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setDraft({
                      id: r.id,
                      sort_order: r.sort_order,
                      match_customer_type: r.match_customer_type,
                      keyword_pattern: r.keyword_pattern ?? "",
                      template_slug: r.template_slug,
                      description: r.description,
                      is_active: r.is_active,
                    })
                  }
                >
                  <Pencil className="mr-1 size-3.5" /> Bewerken
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (confirm(`Regel "${r.description}" verwijderen?`)) delMut.mutate(r.id);
                  }}
                >
                  <Trash2 className="mr-1 size-3.5" /> Verwijder
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Auditlog: wie heeft wanneer een sjabloon/regel aangemaakt, bewerkt, geactiveerd,
// gedeactiveerd, gearchiveerd, hersteld, herordend of verwijderd.
// ---------------------------------------------------------------------------

import { listWaTemplateAudit } from "@/lib/whatsapp-template-audit.functions";
import { History } from "lucide-react";

type AuditEntry = {
  id: string;
  actor_user_id: string | null;
  actor_name: string | null;
  actor_email: string | null;
  entity_type: "prompt_template" | "auto_rule";
  entity_id: string | null;
  entity_slug: string | null;
  action:
    | "created"
    | "updated"
    | "activated"
    | "deactivated"
    | "archived"
    | "unarchived"
    | "reordered"
    | "deleted";
  summary: string | null;
  diff: Record<string, any> | null;
  created_at: string;
};

const ACTION_LABEL: Record<AuditEntry["action"], { label: string; className: string }> = {
  created: { label: "Aangemaakt", className: "bg-emerald-100 text-emerald-800" },
  updated: { label: "Bewerkt", className: "bg-blue-100 text-blue-800" },
  activated: { label: "Geactiveerd", className: "bg-emerald-100 text-emerald-800" },
  deactivated: { label: "Gedeactiveerd", className: "bg-slate-200 text-slate-700" },
  archived: { label: "Gearchiveerd", className: "bg-amber-100 text-amber-800" },
  unarchived: { label: "Hersteld", className: "bg-amber-100 text-amber-800" },
  reordered: { label: "Herordend", className: "bg-indigo-100 text-indigo-800" },
  deleted: { label: "Verwijderd", className: "bg-rose-100 text-rose-800" },
};

function AuditLogViewer() {
  const list = useServerFn(listWaTemplateAudit);
  const [filter, setFilter] = useState<"all" | "prompt_template" | "auto_rule">("all");

  const q = useQuery({
    queryKey: ["admin-wa-template-audit", filter],
    queryFn: () =>
      list({
        data: {
          limit: 100,
          entityType: filter === "all" ? undefined : filter,
        },
      }),
  });

  const entries: AuditEntry[] = useMemo(() => (q.data?.entries ?? []) as AuditEntry[], [q.data]);
  const fmt = (iso: string) =>
    new Date(iso).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" });

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b p-3">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <History className="size-4" /> Auditlog
        </h2>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Filter:</span>
          <select
            className="h-8 rounded-md border bg-background px-2"
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
          >
            <option value="all">Alles</option>
            <option value="prompt_template">Sjablonen</option>
            <option value="auto_rule">Auto-regels</option>
          </select>
        </div>
      </div>

      {q.isLoading ? (
        <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Laden…
        </div>
      ) : q.error ? (
        <div className="p-4 text-sm text-rose-700">{(q.error as Error).message}</div>
      ) : entries.length === 0 ? (
        <div className="p-4 text-sm text-muted-foreground">Nog geen activiteit vastgelegd.</div>
      ) : (
        <ul className="divide-y">
          {entries.map((e) => {
            const meta = ACTION_LABEL[e.action] ?? { label: e.action, className: "bg-slate-100 text-slate-700" };
            const actor =
              e.actor_name || e.actor_email || (e.actor_user_id ? `${e.actor_user_id.slice(0, 8)}…` : "systeem");
            return (
              <li key={e.id} className="grid gap-1 p-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${meta.className}`}>
                    {meta.label}
                  </span>
                  <span className="text-[10px] uppercase tracking-wide text-slate-500">
                    {e.entity_type === "prompt_template" ? "sjabloon" : "auto-regel"}
                  </span>
                  {e.entity_slug && (
                    <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px]">{e.entity_slug}</code>
                  )}
                  <span className="ml-auto text-[11px] text-slate-500">{fmt(e.created_at)}</span>
                </div>
                <div className="text-sm">
                  <span className="font-medium">{actor}</span>
                  {e.summary ? <span className="text-slate-700"> — {e.summary}</span> : null}
                </div>
                {e.diff && Object.keys(e.diff).length > 0 && (
                  <details className="text-[11px] text-slate-600">
                    <summary className="cursor-pointer text-slate-500">Details</summary>
                    <pre className="mt-1 overflow-x-auto rounded bg-slate-50 p-2 text-[11px]">
                      {JSON.stringify(e.diff, null, 2)}
                    </pre>
                  </details>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
